import {
  Box,
  IconButton,
  Grid,
  Typography,
  Button,
  useTheme,
  TextareaAutosize,
} from "@mui/material";
import { memo, useCallback, useEffect, useMemo } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";

import { DropdownV1, ButtonV1 } from "components";
import ErrorMessage from "./ErrorMessage";
import FGBatchRow from "./FGBatchRow";

import { remarkList, inputFieldSx } from "../constants";
import PropTypes from "prop-types";

const parseMT = (value) => Number(String(value || "0").replace(" MT", ""));

const buildBatchDefaults = (originalFgReady, expectedFgData) => {
  if (expectedFgData?.length) {
    return expectedFgData.map((batch, index) => ({
      id: batch.id ?? null,
      fgReadyNow: index === 0 ? originalFgReady : 0,
      qty: batch.quantity ?? "",
      readinessDate: batch.readinessDate ? new Date(batch.readinessDate) : null,
      sellerOrderRef: batch.sellerOrderRef ?? "",
      sellerOLRef: batch.sellerOlRef ?? "",
    }));
  }
  return [
    {
      id: null,
      fgReadyNow: originalFgReady,
      qty: "",
      readinessDate: "",
      sellerOrderRef: "",
      sellerOLRef: "",
    },
  ];
};

// PLANT SUPPLY only: Merge FG ready and expected batches by sellerOrderRef.
// FG ready data drives the rows; unmatched expected batches are appended.
// Blank sellerOrderRef values never match and remain separate.
// Drop FG ready entries with zero quantity.
const mergePlantSupplyBatches = (fgReadyData = [], expectedFgData = []) => {
  const readyRows = (fgReadyData || [])
    .filter((ready) => Number(ready.quantity) > 0)
    .map((ready) => {
      const matchedExpected = ready.sellerOrderRef
        ? (expectedFgData || []).find(
            (expected) => expected.sellerOrderRef === ready.sellerOrderRef,
          )
        : null;

      return {
        id: ready.id ?? null,
        fgReadyQty: ready.quantity ?? 0,
        expectedQty: matchedExpected?.quantity ?? 0,
        sellerOrderRef: ready.sellerOrderRef ?? "",
        sellerOlRef: ready.sellerOlRef ?? "",
        readinessDate: matchedExpected?.readinessDate ?? null,
      };
    });

  const readySellerOrderRefs = new Set(
    readyRows.map((row) => row.sellerOrderRef).filter(Boolean),
  );

  const expectedOnlyRows = (expectedFgData || [])
    .filter(
      (expected) =>
        !expected.sellerOrderRef ||
        !readySellerOrderRefs.has(expected.sellerOrderRef),
    )
    .map((expected) => ({
      id: expected.id ?? null,
      fgReadyQty: 0,
      expectedQty: expected.quantity ?? 0,
      sellerOrderRef: expected.sellerOrderRef ?? "",
      sellerOlRef: expected.sellerOlRef ?? "",
      readinessDate: expected.readinessDate ?? null,
    }));

  return [...readyRows, ...expectedOnlyRows];
};

const buildPlantSupplyBatchDefaults = (mergedBatches) => {
  if (mergedBatches?.length) {
    return mergedBatches.map((batch) => ({
      id: batch.id,
      fgReadyNow: batch.fgReadyQty,
      qty: batch.expectedQty,
      readinessDate: batch.readinessDate ? new Date(batch.readinessDate) : null,
      sellerOrderRef: batch.sellerOrderRef,
      sellerOLRef: batch.sellerOlRef,
      isReadOnly: !!batch.fgReadyQty,
    }));
  }
  return [
    {
      id: null,
      fgReadyNow: 0,
      qty: "",
      readinessDate: "",
      sellerOrderRef: "",
      sellerOLRef: "",
      isReadOnly: false,
    },
  ];
};

const FGUpdate = ({
  lineItem,
  onSubmit,
  handleToggleFgUpdate,
  fgUpdateData,
  orderType,
}) => {
  const theme = useTheme();
  const originalFgReady = fgUpdateData?.fgReadyQty ?? 0;
  const isPlantSupply = orderType === "PLANT SUPPLY";

  const plantSupplyBatches = useMemo(
    () =>
      isPlantSupply
        ? mergePlantSupplyBatches(
            fgUpdateData?.fgReadyData,
            fgUpdateData?.expectedFgData,
          )
        : [],
    [isPlantSupply, fgUpdateData],
  );

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: {
      reason: "",
      otherReason: "",
      fgBatches: isPlantSupply
        ? buildPlantSupplyBatchDefaults(plantSupplyBatches)
        : buildBatchDefaults(originalFgReady, fgUpdateData?.expectedFgData),
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "fgBatches",
  });

  const handleAddBatch = useCallback(() => {
    append({
      id: null,
      fgReadyNow: 0,
      qty: "",
      readinessDate: null,
      sellerOrderRef: "",
      sellerOLRef: "",
      isReadOnly: false,
    });
  }, [append]);

  const allBatches = watch("fgBatches") || [];
  const reason = watch("reason");
  const otherReason = watch("otherReason");
  const isOtherSelected = reason === "Other Reasons";
  const originalBatchCount = isPlantSupply
    ? plantSupplyBatches.length
    : fgUpdateData?.expectedFgData?.length || 0;
  const hasPreDoId = !!fgUpdateData?.preDoId;

  const { today, maxAllowedDate } = useMemo(() => {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const maxDate = new Date(currentDate);
    maxDate.setDate(currentDate.getDate() + 3);

    return {
      today: currentDate,
      maxAllowedDate: maxDate,
    };
  }, []);

  const totalFgReadyNow = allBatches.reduce(
    (sum, item) => sum + (Number(item?.fgReadyNow) || 0),
    0,
  );

  const totalExpectedQty = allBatches.reduce(
    (sum, item) => sum + (Number(item?.qty) || 0),
    0,
  );

  const orderQty = parseMT(lineItem?.orderQty);
  const nonInvoicedQty = parseMT(lineItem?.notInvoicedQty);
  const remainingQty = Math.max(
    Number.parseFloat(
      (nonInvoicedQty - totalFgReadyNow - totalExpectedQty).toFixed(10),
    ),
    0,
  );

  const showReductionAlert = totalFgReadyNow < originalFgReady;
  const isDecreased = totalFgReadyNow < originalFgReady;
  const firstBatchFgReadyNow = Number(allBatches[0]?.fgReadyNow) || 0;
  const isFgReadyNowIncreased =
    originalFgReady > 0 && firstBatchFgReadyNow > originalFgReady;
  const isQtyExceeded = totalFgReadyNow + totalExpectedQty > orderQty;
  const isQtyExceededPlantSupply =
    totalFgReadyNow + totalExpectedQty > orderQty * 1.2;

  const isMissingReadinessDate = allBatches.some((batch) => {
    if (isPlantSupply && batch?.isReadOnly) return false;

    const qtyEntered =
      batch?.qty !== "" && batch?.qty !== null && batch?.qty !== undefined;
    return qtyEntered && !batch?.readinessDate;
  });

  const isMissingSellerOLRef =
    isPlantSupply &&
    allBatches.some(
      (batch) =>
        !batch?.isReadOnly &&
        !!batch?.sellerOrderRef?.trim() &&
        !batch?.sellerOLRef?.trim(),
    );

  const isSubmitDisabled =
    (isPlantSupply ? isQtyExceededPlantSupply : isQtyExceeded) ||
    isFgReadyNowIncreased ||
    (hasPreDoId && isDecreased) ||
    (!hasPreDoId && showReductionAlert && !reason) ||
    (isOtherSelected && !otherReason?.trim()) ||
    isMissingReadinessDate ||
    isMissingSellerOLRef;

  useEffect(() => {
    if (fgUpdateData) {
      const fgReady = fgUpdateData?.fgReadyQty ?? 0;
      reset({
        reason: "",
        otherReason: "",
        fgBatches: isPlantSupply
          ? buildPlantSupplyBatchDefaults(plantSupplyBatches)
          : buildBatchDefaults(fgReady, fgUpdateData?.expectedFgData),
      });
    }
  }, [fgUpdateData, isPlantSupply, plantSupplyBatches, reset]);

  const submitHandler = ({ otherReason, fgBatches, ...rest }) => {
    const fgReadyNowTotal = fgBatches.reduce(
      (sum, b) => sum + (Number(b?.fgReadyNow) || 0),
      0,
    );
    const payload = {
      ...rest,
      fgReadyNow: fgReadyNowTotal,
      fgBatches,
      reason:
        rest.reason === "Other Reasons" ? otherReason.trim() : rest.reason,
    };
    onSubmit(payload);
  };

  const handleRemoveBatch = (index) => {
    remove(index);
    setValue("reason", "");
    setValue("otherReason", "");
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === "-" || e.key === "e" || e.key === "E") {
      e.preventDefault();
    }
  }, []);

  return (
    <Box sx={{ p: 3, pb: 0, backgroundColor: "#f7f8fc", borderRadius: 2 }}>
      <Typography
        variant="body2"
        color="primary.main"
        fontWeight={700}
        sx={{ mb: 2 }}
      >
        {`FG READINESS UPDATE · ${lineItem.id} · ${lineItem.orderQty}`}
      </Typography>

      <form onSubmit={handleSubmit(submitHandler)}>
        {!hasPreDoId && showReductionAlert && (
          <ErrorMessage message="⚠ FG reduction detected. Reducing FG ready below the current value will be logged as an exception. Reason is mandatory." />
        )}

        {hasPreDoId && isDecreased && (
          <ErrorMessage
            message={`FG ready qty can't go below ${originalFgReady} MT as a pre-DO already
            exists for this line`}
          />
        )}

        {/* Validation on first field only: pre-filled FG ready cannot be increased */}
        {isFgReadyNowIncreased && (
          <ErrorMessage
            message={`FG Ready now cannot exceed the existing FG Ready quantity. To increase it, add a new FG Batch.`}
          />
        )}

        {isQtyExceededPlantSupply && isPlantSupply && (
          <ErrorMessage message="Expected FG quantity exceeds the available quantity for this SLO Order Line." />
        )}

        {isQtyExceeded && !isPlantSupply && (
          <ErrorMessage
            message={`Total FG ready and expected (${Number.parseFloat(
              (totalFgReadyNow + totalExpectedQty).toFixed(10),
            )} MT) exceeds
            order qty (${orderQty} MT), enter a lower quantity to continue.`}
          />
        )}

        {isMissingReadinessDate && (
          <ErrorMessage message="Readiness date is required for every FG batch with a quantity" />
        )}

        {isMissingSellerOLRef && (
          <ErrorMessage message="SO OL Ref is required to complete line-level mapping." />
        )}

        {isOtherSelected && !otherReason?.trim() && (
          <ErrorMessage message="Reason is required when 'Other Reasons' is selected" />
        )}

        {fields.map((field, index) => {
          const isLastRow = index === fields.length - 1;
          const isRowLocked = isPlantSupply && !!allBatches[index]?.isReadOnly;

          return (
            <FGBatchRow
              key={field.id}
              control={control}
              index={index}
              disabled={isRowLocked}
              disableFgReadyNow={isPlantSupply}
              remainingQty={remainingQty}
              isLastRow={isLastRow}
              originalBatchCount={originalBatchCount}
              hasPreDoId={hasPreDoId}
              today={today}
              maxAllowedDate={maxAllowedDate}
              handleKeyDown={handleKeyDown}
            >
              {isLastRow && (
                <Grid item xs={12} md={2}>
                  <Controller
                    name="reason"
                    control={control}
                    render={({ field }) => (
                      <DropdownV1
                        {...field}
                        placeholder="-- Select reason --"
                        itemList={remarkList}
                        listType="array"
                        sx={inputFieldSx}
                        disabled={isRowLocked}
                        onChange={(val) => {
                          const selectedValue = val?.target
                            ? val.target.value
                            : val;
                          if (selectedValue !== "Other Reasons") {
                            setValue("otherReason", "");
                          }
                          field.onChange(selectedValue);
                        }}
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={0.5}>
                {index >= originalBatchCount && (
                  <IconButton
                    color="black"
                    onClick={() => handleRemoveBatch(index)}
                  >
                    <Typography>x</Typography>
                  </IconButton>
                )}
              </Grid>
            </FGBatchRow>
          );
        })}
        {isOtherSelected && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 3,
              mr: 12,
            }}
          >
            <Controller
              name="otherReason"
              control={control}
              render={({ field }) => (
                <TextareaAutosize
                  {...field}
                  minRows={2}
                  placeholder="Enter Reason"
                  style={{
                    width: 300,
                    padding: "8px",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    border: `1px solid ${theme.palette.text.secondary}`,
                    borderRadius: "4px",
                    resize: "none",
                    outline: "none",
                  }}
                />
              )}
            />
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mt: 6,
            mb: 1,
          }}
        >
          <Box>
            <Button
              variant="outlined"
              onClick={handleAddBatch}
              sx={{
                height: 30,
                fontSize: 12,
              }}
            >
              + Add FG Batch
            </Button>
            <Typography sx={{ mt: 3, color: "grey", fontSize: 12 }}>
              {`Total FG cannot exceed order qty (${orderQty} MT)`}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <ButtonV1
              type="button"
              variant="outlined"
              title="Cancel"
              onClick={() => handleToggleFgUpdate(lineItem)}
              style={{
                minWidth: 70,
                height: 30,
                fontSize: 12,
                px: 1.5,
              }}
            />
            <ButtonV1
              type="submit"
              disabled={isSubmitDisabled}
              variant={showReductionAlert ? "outlined" : "contained"}
              title={
                showReductionAlert ? "Reduce FG (raise exception)" : "Submit"
              }
              sx={{
                minWidth: 70,
                height: 30,
                fontSize: 12,
                px: 1.5,
                ...(showReductionAlert && !isSubmitDisabled
                  ? {
                      color: `${theme.palette.error.dark} !important`,
                      borderColor: `${theme.palette.error.dark} !important`,
                    }
                  : {}),
                "&.Mui-disabled": {
                  pointerEvents: "auto",
                  cursor: "default",
                  backgroundColor: `${theme.palette.action.disabledBackground} !important`,
                },
              }}
            />
          </Box>
        </Box>
      </form>
    </Box>
  );
};

FGUpdate.propTypes = {
  lineItem: PropTypes.object,
  fgUpdateData: PropTypes.object,
  onSubmit: PropTypes.func,
  handleToggleFgUpdate: PropTypes.func,
  orderType: PropTypes.string,
};

export default memo(FGUpdate);
